import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';
import { isAdLabelYes } from '../../utils/adLabel';
import { ExampleLinkIcon } from '../catalog/ExampleLinkIcon';

const seo = (p, k) => (p.seo_metrics && p.seo_metrics[k] !== null && p.seo_metrics[k] !== undefined ? p.seo_metrics[k] : null);

const SortHead = ({ field, label, className, sortField, sortOrder, handleSort }) => (
  <TableHead className={`cursor-pointer hover:bg-muted/50 select-none ${className}`} onClick={() => handleSort(field)} data-testid={`admin-sort-${field}`}>
    <div className="flex items-center gap-1">
      {label}
      {sortField === field ? (sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 text-muted-foreground" />}
    </div>
  </TableHead>
);

export const AdminProductsTable = ({
  products,
  pagination,
  selectedProducts,
  visibleColumns,
  sortField,
  sortOrder,
  handleSort,
  handleSelectAll,
  handleSelectProduct,
  openProductDialog,
  handleProductDelete,
  fetchProducts,
  language,
  texts,
}) => {
  return (
    <>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={products.length > 0 && selectedProducts.size === products.length}
                    onCheckedChange={handleSelectAll}
                    aria-label={selectedProducts.size === products.length ? texts.deselectAll : texts.selectAll}
                  />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('website')}
                >
                  <div className="flex items-center gap-1">
                    {texts.website}
                    {sortField === 'website' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </TableHead>
                {visibleColumns.email && <TableHead>Email</TableHead>}
                {visibleColumns.country && <TableHead>{texts.country}</TableHead>}
                {visibleColumns.pvp && <TableHead>PVP</TableHead>}
                {visibleColumns.pc && <TableHead>PC</TableHead>}
                {visibleColumns.premiumV && <TableHead>Prem.V</TableHead>}
                {visibleColumns.premiumC && <TableHead>Prem.C</TableHead>}
                {visibleColumns.insercPvp && <TableHead className="text-indigo-700" title={language === 'es' ? 'PVP Inserción normal' : 'Insertion PVP normal'}>Inserc.</TableHead>}
                {visibleColumns.insercPvpS && <TableHead className="text-indigo-700" title={language === 'es' ? 'PVP Inserción sensible' : 'Insertion PVP sensitive'}>Inserc. S.</TableHead>}
                {visibleColumns.mencPvp && <TableHead className="text-rose-700" title={language === 'es' ? 'PVP Mención normal' : 'Mention PVP normal'}>Menc.</TableHead>}
                {visibleColumns.mencPvpS && <TableHead className="text-rose-700" title={language === 'es' ? 'PVP Mención sensible' : 'Mention PVP sensitive'}>Menc. S.</TableHead>}
                {visibleColumns.dr && <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('dr')}
                >
                  <div className="flex items-center gap-1">
                    {texts.dr}
                    {sortField === 'dr' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </TableHead>}
                {visibleColumns.traffic && <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('organic_traffic')}
                >
                  <div className="flex items-center gap-1">
                    {language === 'es' ? 'Tráfico' : 'Traffic'}
                    {sortField === 'organic_traffic' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </TableHead>}
                {visibleColumns.tf && <SortHead field="tf" label="TF" className="text-orange-600" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />}
                {visibleColumns.rd && <SortHead field="rd" label="RD" className="text-rose-600" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />}
                {visibleColumns.kw && <SortHead field="keywords" label="KW" className="text-red-600" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort} />}
                {visibleColumns.gn && <TableHead>GN</TableHead>}
                {visibleColumns.available && <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('available')}
                >
                  <div className="flex items-center gap-1">
                    {texts.available}
                    {sortField === 'available' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </TableHead>}
                {visibleColumns.linkType && <TableHead>{language === 'es' ? 'Tipo Enlace' : 'Link Type'}</TableHead>}
                {visibleColumns.duration && <TableHead>{language === 'es' ? 'Duración' : 'Duration'}</TableHead>}
                {visibleColumns.label && <TableHead>{language === 'es' ? 'Etiqueta' : 'Label'}</TableHead>}
                {visibleColumns.sensitive && <TableHead className="text-orange-600">{language === 'es' ? 'Temáticas Sensibles' : 'Sensitive Topics'}</TableHead>}
                {visibleColumns.infoPC && <TableHead className="text-blue-600">{language === 'es' ? 'Info PC' : 'PC Info'}</TableHead>}
                {visibleColumns.secondMail && <TableHead className="text-blue-600">{language === 'es' ? '2º Mail' : '2nd Mail'}</TableHead>}
                {visibleColumns.deleteReason && <TableHead className="text-red-600">{language === 'es' ? 'Motivo Elim.' : 'Del. Reason'}</TableHead>}
                <TableHead className="text-right">{texts.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={24} className="text-center py-8 text-muted-foreground">
                    {texts.noProducts}
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => (
                  <TableRow 
                    key={p.id} 
                    className={selectedProducts.has(p.id) ? 'bg-primary/5' : ''}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedProducts.has(p.id)}
                        onCheckedChange={() => handleSelectProduct(p.id)}
                        aria-label={`Select ${p.website}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium max-w-[180px] truncate">{p.website}</TableCell>
                    {visibleColumns.email && <TableCell className="max-w-[150px] truncate text-xs text-muted-foreground">{p.web_email || '-'}</TableCell>}
                    {visibleColumns.country && <TableCell className="text-xs">{p.country}</TableCell>}
                    {visibleColumns.pvp && <TableCell className="text-green-600 font-medium">€{p.pvp || p.price || 0}</TableCell>}
                    {visibleColumns.pc && <TableCell className="text-red-600 font-medium whitespace-nowrap">
                      {(() => {
                        const cur = (p.pc_currency || 'EUR').toUpperCase();
                        const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
                        return (
                          <span className="inline-flex items-center gap-1">
                            <span>{sym}{p.pc || 0}</span>
                            {cur !== 'EUR' && (
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title={`Moneda: ${cur}`}>{cur}</span>
                            )}
                          </span>
                        );
                      })()}
                    </TableCell>}
                    {visibleColumns.premiumV && <TableCell className="text-orange-500 font-medium">€{p.premium_sale_price || p.price_sensitive || 0}</TableCell>}
                    {visibleColumns.premiumC && <TableCell className="text-purple-600 font-medium">€{p.premium_purchase_price || 0}</TableCell>}
                    {visibleColumns.insercPvp && <TableCell className="font-medium">{p.allows_insertion && p.pvp_insertion > 0 ? <span className="text-indigo-700">€{p.pvp_insertion}</span> : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.insercPvpS && <TableCell className="font-medium">{p.allows_insertion && p.pvp_insertion_sensitive > 0 ? <span className="text-amber-600">€{p.pvp_insertion_sensitive}</span> : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.mencPvp && <TableCell className="font-medium">{p.allows_mention && p.pvp_mention > 0 ? <span className="text-rose-700">€{p.pvp_mention}</span> : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.mencPvpS && <TableCell className="font-medium">{p.allows_mention && p.pvp_mention_sensitive > 0 ? <span className="text-amber-600">€{p.pvp_mention_sensitive}</span> : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.dr && <TableCell>{p.dr}</TableCell>}
                    {visibleColumns.traffic && <TableCell className="text-sky-600">{(p.organic_traffic || 0).toLocaleString()}</TableCell>}
                    {visibleColumns.tf && <TableCell className="text-orange-600 font-medium">{seo(p, 'tf') ?? <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.rd && <TableCell className="text-rose-600 font-medium">{seo(p, 'rd') !== null ? seo(p, 'rd').toLocaleString() : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.kw && <TableCell className="text-red-600 font-medium">{seo(p, 'keywords') !== null ? seo(p, 'keywords').toLocaleString() : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.gn && <TableCell>{seo(p, 'gg_news') ? <Newspaper className="h-4 w-4 text-rose-500" /> : <span className="text-slate-300">—</span>}</TableCell>}
                    {visibleColumns.available && <TableCell>
                      <Badge variant={p.available ? 'outline' : 'destructive'}>
                        {p.available ? 'Sí' : 'No'}
                      </Badge>
                    </TableCell>}
                    {visibleColumns.linkType && <TableCell className="text-xs">
                      {p.link_type || '-'}
                    </TableCell>}
                    {visibleColumns.duration && <TableCell className="text-xs">
                      <Badge variant={p.link_duration?.toLowerCase() === 'permanente' ? 'default' : 'secondary'} className={p.link_duration?.toLowerCase() === 'permanente' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {p.link_duration || '-'}
                      </Badge>
                    </TableCell>}
                    {visibleColumns.label && <TableCell className="text-xs">
                      <Badge className={isAdLabelYes(p.ad_label) ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}>
                        {isAdLabelYes(p.ad_label) ? (p.ad_label || 'SI') : 'NO'}
                      </Badge>
                    </TableCell>}
                    {visibleColumns.sensitive && <TableCell className="text-xs max-w-[220px]">
                      {(() => {
                        const topics = ['casino','gambling','cbd','cannabis','cryptocurrency','sex','erotic','porn','vpn','tarot'].filter(k => !!p[k]);
                        if (topics.length === 0) {
                          return (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium"
                              data-testid={`product-sensitive-${p.id}`}
                            >
                              {language === 'es' ? 'No acepta' : "Doesn't accept"}
                            </span>
                          );
                        }
                        return (
                          <div className="flex flex-wrap gap-1" data-testid={`product-sensitive-${p.id}`}>
                            {topics.map(tk => (
                              <span key={tk} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                                {getSensitiveTopicLabel(tk, language)}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </TableCell>}
                    {visibleColumns.infoPC && <TableCell className="text-xs max-w-[100px] truncate text-blue-600" title={p.mas_info_pc || ''}>
                      {p.mas_info_pc || '-'}
                    </TableCell>}
                    {visibleColumns.secondMail && <TableCell className="text-xs max-w-[120px] truncate text-blue-600">
                      {p.second_email || '-'}
                    </TableCell>}
                    {visibleColumns.deleteReason && <TableCell className="text-xs max-w-[100px] truncate text-red-600">
                      {p.motivo_eliminacion || '-'}
                    </TableCell>}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <ExampleLinkIcon url={p.publication_example_url} testId={`admin-product-example-${p.id}`} />
                        <Button variant="ghost" size="sm" onClick={() => openProductDialog(p)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleProductDelete(p.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchProducts(pagination.page - 1)}
            disabled={!pagination.has_prev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            {texts.page} {pagination.page} {texts.of} {pagination.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchProducts(pagination.page + 1)}
            disabled={!pagination.has_next}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </>
  );
};

export default AdminProductsTable;
